import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import ConstructionIcon from "@mui/icons-material/Construction";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import DevicesIcon from "@mui/icons-material/Devices";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import FactoryIcon from "@mui/icons-material/Factory";
import FlightIcon from "@mui/icons-material/Flight";
import GavelIcon from "@mui/icons-material/Gavel";
import HomeIcon from "@mui/icons-material/Home";
import LocalGroceryStoreIcon from "@mui/icons-material/LocalGroceryStore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SchoolIcon from "@mui/icons-material/School";
import SecurityIcon from "@mui/icons-material/Security";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SpaIcon from "@mui/icons-material/Spa";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import {
  Alert,
  Box,
  CircularProgress,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useFacilitiesQuery } from "Api/Queries/Merchants/useFacilitiesQuery";
import { ItemCardLink } from "Components/Shared/ItemCardLink/ItemCardLink";
import { StoreFilters } from "Components/WhereToPay/StoresSection/StoreFilters";
import type { CategoryDto } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { getFormattedAddress } from "Utils/AddressUtils";
import { appUrl } from "Utils/UrlUtils";
import { useSearchParams } from "Navigation/useSearchParams";
import {
  CATEGORY_PARAM,
  MUNICIPALITY_PARAM,
  PAGE_NUMBER_PARAM,
} from "Components/WhereToPay/StoresSection/StoreParams";

const RESOURCES = Resources.Pages.Content.WhereToPay.StoresSection;
const PAGE_SIZE = 20;

const CATEGORY_ICON_MAP: Record<string, typeof RestaurantIcon> = {
  Restaurants: RestaurantIcon,
  Food: LocalGroceryStoreIcon,
  Car: DirectionsCarIcon,
  Electronics: DevicesIcon,
  ClothingAndShoes: CheckroomIcon,
  HealthAndBeauty: SpaIcon,
  Finance: AccountBalanceIcon,
  LeisureTime: SportsEsportsIcon,
  PhoneTvAndInternet: PhoneIphoneIcon,
  Housing: HomeIcon,
  Travel: FlightIcon,
  Insurance: SecurityIcon,
  Education: SchoolIcon,
  Institutions: BusinessIcon,
  ConstructionAndMaintenance: ConstructionIcon,
  ECommerceAndOnlineServices: ShoppingCartIcon,
  PersonalServices: ContentCutIcon,
  LegalAndConsultingServices: GavelIcon,
  IndustrialProductionAndProcessing: FactoryIcon,
  Other: MoreHorizIcon,
};

const getCategoryIcon = (categories: CategoryDto[]) => {
  const firstCategory = categories[0]?.code;
  const IconComponent = firstCategory
    ? (CATEGORY_ICON_MAP[firstCategory] ?? MoreHorizIcon)
    : MoreHorizIcon;
  return <IconComponent sx={{ color: "grey.600" }} />;
};

export const StoresSection = () => {
  const { t } = useResource();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filter values from URL params
  const categoryFilter = searchParams.get(CATEGORY_PARAM);
  const municipalityFilter = searchParams.get(MUNICIPALITY_PARAM);
  const pageParam = searchParams.get(PAGE_NUMBER_PARAM);
  const currentPage = Math.max(1, Number(pageParam) || 1);

  const {
    data: facilitiesData,
    isLoading,
    error,
  } = useFacilitiesQuery(
    categoryFilter || undefined,
    municipalityFilter || undefined,
    PAGE_SIZE,
    currentPage,
  );

  const totalCount = facilitiesData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page > 1) {
        next.set(PAGE_NUMBER_PARAM, String(page));
      } else {
        next.delete(PAGE_NUMBER_PARAM);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" py={4}>
          <Alert severity="error">
            {t(Resources.Merchant.DataFailedToLoad)}
          </Alert>
        </Box>
      </Layout>
    );
  }

  if (!facilitiesData?.items.length) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" py={4}>
          <Typography color="text.secondary" textAlign="center">
            {t(RESOURCES.NoStoresFound)}
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      {facilitiesData.items.map((facility) => (
        <ItemCardLink
          key={facility.publicID}
          title={facility.name}
          subtitle={
            facility.address ? getFormattedAddress(facility.address) : ""
          }
          icon={getCategoryIcon(facility.categories)}
          to={`${appUrl("store-detail")}/${facility.publicID}`}
        />
      ))}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => (
  <Stack gap={2} mt={2}>
    <StoreFilters />
    {children}
  </Stack>
);
